interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: MemoryInfo;
}

export class MemoryMonitor {
  private static instance: MemoryMonitor;
  private enabled = false;
  private intervalId: number | null = null;
  private callbacks: Array<(memoryInfo: MemoryInfo) => void> = [];
  private warningThreshold = 0.8; // 80% of heap limit
  private lastWarningTime = 0;
  private warningCooldown = 10000; // 10 seconds

  private constructor() {
    this.enabled =
      process.env.NODE_ENV === "development" &&
      typeof window !== "undefined" &&
      "performance" in window &&
      "memory" in (window.performance as PerformanceWithMemory);
  }

  static getInstance(): MemoryMonitor {
    if (!MemoryMonitor.instance) {
      MemoryMonitor.instance = new MemoryMonitor();
    }
    return MemoryMonitor.instance;
  }

  start(intervalMs: number = 5000): void {
    if (!this.enabled) return;

    this.stop();

    this.intervalId = window.setInterval(() => {
      this.checkMemory();
    }, intervalMs);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  onMemoryUpdate(callback: (memoryInfo: MemoryInfo) => void): void {
    this.callbacks.push(callback);
  }

  private checkMemory(): void {
    if (!this.enabled) return;

    const perf = window.performance as PerformanceWithMemory;
    const memory = perf.memory;

    if (!memory) return;

    const memoryInfo = {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
    };

    const usageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
    const now = Date.now();

    if (
      usageRatio > this.warningThreshold &&
      now - this.lastWarningTime > this.warningCooldown
    ) {
      console.warn(`⚠️ High memory usage: ${(usageRatio * 100).toFixed(1)}%`);
      console.warn(
        `Used: ${this.formatBytes(
          memory.usedJSHeapSize,
        )}, Limit: ${this.formatBytes(memory.jsHeapSizeLimit)}`,
      );
      this.lastWarningTime = now;
    }

    this.callbacks.forEach(callback => callback(memoryInfo));
  }

  getMemoryInfo(): MemoryInfo | null {
    if (!this.enabled) return null;

    const perf = window.performance as PerformanceWithMemory;
    return perf.memory || null;
  }

  private formatBytes(bytes: number): string {
    const MB = 1024 * 1024;
    return `${(bytes / MB).toFixed(1)}MB`;
  }

  logMemoryStats(): void {
    const memory = this.getMemoryInfo();
    if (!memory) {
      console.log("Memory API not available");
      return;
    }

    console.log("Memory Stats:");
    console.log(`Used: ${this.formatBytes(memory.usedJSHeapSize)}`);
    console.log(`Total: ${this.formatBytes(memory.totalJSHeapSize)}`);
    console.log(`Limit: ${this.formatBytes(memory.jsHeapSizeLimit)}`);
    console.log(
      `Usage: ${(
        (memory.usedJSHeapSize / memory.jsHeapSizeLimit) *
        100
      ).toFixed(1)}%`,
    );
  }
}

export const memoryMonitor = MemoryMonitor.getInstance();

export function startMemoryMonitoring(intervalMs?: number): void {
  memoryMonitor.start(intervalMs);
}

export function stopMemoryMonitoring(): void {
  memoryMonitor.stop();
}

export function logMemoryStats(): void {
  memoryMonitor.logMemoryStats();
}

if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  setTimeout(() => {
    startMemoryMonitoring();
  }, 2000);
}
